import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, ImagePlus, Loader2 } from "lucide-react";
import api from "@/api";

const inputCls = "rounded-lg border border-clayline bg-cream px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-terracotta/60";
const imgSrc = (p) => (!p ? "" : (p.startsWith("http") || p.startsWith("/")) ? p : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`);

const Section = ({ title, children, testId }) => (
  <div className="bg-cream border border-clayline rounded-2xl p-6" data-testid={testId}>
    <h3 className="font-serif font-bold text-xl italic mb-5">{title}</h3>
    <div className="space-y-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, textarea = false, testId }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-charcoal/50 block mb-1">{label}</span>
    {textarea ? (
      <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />
    ) : (
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />
    )}
  </label>
);

const FranchiseManager = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);

  useEffect(() => {
    api.get("/franchise").then((r) => setContent(r.data)).catch(() => toast.error("Failed to load franchise content"));
  }, []);

  if (!content) return <p className="font-mono text-sm text-charcoal/50" data-testid="franchise-manager-loading">Loading…</p>;

  const update = (path, value) =>
    setContent((c) => {
      const n = JSON.parse(JSON.stringify(c));
      let o = n;
      for (let i = 0; i < path.length - 1; i++) o = o[path[i]];
      o[path[path.length - 1]] = value;
      return n;
    });

  const uploadStoreImage = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      update(["store_types", idx, "image"], data.path);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingIdx(null);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/franchise", content);
      toast.success("Franchise page updated — live instantly");
    } catch {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="franchise-manager" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">Hero, FICO / FOCO / FOFO models, comparison content and partner pillars</p>
        <button onClick={save} disabled={saving} data-testid="franchise-save-btn" className="rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] bg-terracotta text-cream hover:bg-charcoal transition-colors inline-flex items-center gap-2 disabled:opacity-60">
          <Check size={14} /> {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>

      <Section title="Hero" testId="fm-section-hero">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Eyebrow tag" value={content.eyebrow} onChange={(v) => update(["eyebrow"], v)} testId="fm-hero-eyebrow" />
          <Field label="Subtitle" value={content.subtitle} onChange={(v) => update(["subtitle"], v)} testId="fm-hero-subtitle" />
          <Field label="Headline (dark part)" value={content.title_dark} onChange={(v) => update(["title_dark"], v)} testId="fm-hero-title-dark" />
          <Field label="Headline (terracotta italic part)" value={content.title_accent} onChange={(v) => update(["title_accent"], v)} testId="fm-hero-title-accent" />
        </div>
        <Field label="Brand introduction" value={content.brand_intro} onChange={(v) => update(["brand_intro"], v)} textarea testId="fm-brand-intro" />
        <Field label="Brand features (one per line)" value={(content.brand_features || []).join("\n")} onChange={(v) => update(["brand_features"], v.split("\n").filter(Boolean))} textarea testId="fm-brand-features" />
      </Section>

      <Section title="Hero Stat Cards (4)" testId="fm-section-hero-stats">
        {(content.hero_stats || []).map((s, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end border-b border-clayline/50 pb-3" data-testid={`fm-hero-stat-${i}`}>
            <Field label={`#${i + 1} Value`} value={s.value} onChange={(v) => update(["hero_stats", i, "value"], v)} testId={`fm-hero-stat-value-${i}`} />
            <Field label="Label" value={s.label} onChange={(v) => update(["hero_stats", i, "label"], v)} testId={`fm-hero-stat-label-${i}`} />
            <Field label="Sub" value={s.sub} onChange={(v) => update(["hero_stats", i, "sub"], v)} testId={`fm-hero-stat-sub-${i}`} />
            <label className="flex items-center gap-2 pb-2 text-sm text-charcoal/70">
              <input type="checkbox" checked={!!s.accent} onChange={(e) => update(["hero_stats", i, "accent"], e.target.checked)} className="accent-terracotta w-4 h-4" data-testid={`fm-hero-stat-accent-${i}`} />
              Terracotta accent
            </label>
          </div>
        ))}
      </Section>

      <Section title="Traction Row (3)" testId="fm-section-traction">
        {(content.traction || []).map((t, i) => (
          <div key={i} className="grid sm:grid-cols-3 gap-3 border-b border-clayline/50 pb-3" data-testid={`fm-traction-${i}`}>
            <Field label={`#${i + 1} Value`} value={t.value} onChange={(v) => update(["traction", i, "value"], v)} testId={`fm-traction-value-${i}`} />
            <Field label="Label" value={t.label} onChange={(v) => update(["traction", i, "label"], v)} testId={`fm-traction-label-${i}`} />
            <Field label="Sub" value={t.sub} onChange={(v) => update(["traction", i, "sub"], v)} testId={`fm-traction-sub-${i}`} />
          </div>
        ))}
      </Section>

      <Section title="Franchise Models (FICO / FOCO / FOFO)" testId="fm-section-models">
        {(content.models || []).map((model, i) => (
          <div key={model.code || i} className="border border-clayline/60 rounded-xl p-4 space-y-3 bg-sand/30" data-testid={`fm-model-${i}`}>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Code" value={model.code} onChange={(v) => update(["models", i, "code"], v)} testId={`fm-model-code-${i}`} />
              <Field label="Full name" value={model.name} onChange={(v) => update(["models", i, "name"], v)} testId={`fm-model-name-${i}`} />
              <Field label="Tagline" value={model.tagline} onChange={(v) => update(["models", i, "tagline"], v)} testId={`fm-model-tagline-${i}`} />
            </div>
            <Field label="Intro" value={model.intro} onChange={(v) => update(["models", i, "intro"], v)} textarea testId={`fm-model-intro-${i}`} />
            <Field label="Features (one per line)" value={(model.features || []).join("\n")} onChange={(v) => update(["models", i, "features"], v.split("\n").filter(Boolean))} textarea testId={`fm-model-features-${i}`} />
            <div className="space-y-3">
              {(model.details || []).map((item, di) => (
                <div key={di} className="grid sm:grid-cols-3 gap-3 border-t border-clayline/50 pt-3" data-testid={`fm-model-detail-${i}-${di}`}>
                  <Field label={`Detail #${di + 1} label`} value={item.label} onChange={(v) => update(["models", i, "details", di, "label"], v)} testId={`fm-model-detail-label-${i}-${di}`} />
                  <Field label="Value" value={item.value} onChange={(v) => update(["models", i, "details", di, "value"], v)} testId={`fm-model-detail-value-${i}-${di}`} />
                  <Field label="Subtext" value={item.sub} onChange={(v) => update(["models", i, "details", di, "sub"], v)} testId={`fm-model-detail-sub-${i}-${di}`} />
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Note" value={model.note} onChange={(v) => update(["models", i, "note"], v)} textarea testId={`fm-model-note-${i}`} />
              <Field label="CTA button" value={model.cta} onChange={(v) => update(["models", i, "cta"], v)} testId={`fm-model-cta-${i}`} />
            </div>
          </div>
        ))}
      </Section>

      <Section title="Store Types (3 Formats)" testId="fm-section-store-types">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Section title" value={content.store_title} onChange={(v) => update(["store_title"], v)} testId="fm-store-types-title" />
          <Field label="Section subtitle" value={content.store_sub} onChange={(v) => update(["store_sub"], v)} testId="fm-store-types-subtitle" />
        </div>
        {(content.store_types || []).map((store, i) => (
          <div key={store.name || i} className="border border-clayline/60 rounded-xl p-4 space-y-3 bg-sand/30" data-testid={`fm-store-type-${i}`}>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Name" value={store.name} onChange={(v) => update(["store_types", i, "name"], v)} testId={`fm-store-type-name-${i}`} />
              <Field label="Area" value={store.area} onChange={(v) => update(["store_types", i, "area"], v)} testId={`fm-store-type-area-${i}`} />
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-charcoal/50 block mb-1">Image</span>
                {store.image ? (
                  <div className="flex items-center gap-3">
                    <img src={imgSrc(store.image)} alt={`${store.name} preview`} className="w-16 h-11 rounded-lg object-cover border border-clayline" data-testid={`fm-store-type-image-preview-${i}`} />
                    <button type="button" onClick={() => update(["store_types", i, "image"], "")} className="font-mono text-[11px] uppercase tracking-wider text-red-600 hover:underline" data-testid={`fm-store-type-image-remove-${i}`}>Remove</button>
                  </div>
                ) : (
                  <label className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] border border-dashed border-terracotta text-terracotta cursor-pointer hover:bg-terracotta hover:text-cream transition-colors" data-testid={`fm-store-type-image-${i}`}>
                    {uploadingIdx === i ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                    {uploadingIdx === i ? "Uploading…" : "Upload"}
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadStoreImage(i, e)} data-testid={`fm-store-type-image-input-${i}`} />
                  </label>
                )}
              </div>
            </div>
            <Field label="Description" value={store.desc} onChange={(v) => update(["store_types", i, "desc"], v)} textarea testId={`fm-store-type-desc-${i}`} />
            <Field label="Key features (one per line)" value={(store.features || []).join("\n")} onChange={(v) => update(["store_types", i, "features"], v.split("\n").filter(Boolean))} textarea testId={`fm-store-type-features-${i}`} />
          </div>
        ))}
      </Section>

      <Section title="Why Partner With Chai Times" testId="fm-section-why-partner">
        {(content.why_partner || []).map((item, i) => (
          <div key={i} className="grid sm:grid-cols-2 gap-3 border-b border-clayline/50 pb-3" data-testid={`fm-why-partner-${i}`}>
            <Field label={`#${i + 1} Title`} value={item.title} onChange={(v) => update(["why_partner", i, "title"], v)} testId={`fm-why-title-${i}`} />
            <Field label="Description" value={item.desc} onChange={(v) => update(["why_partner", i, "desc"], v)} testId={`fm-why-desc-${i}`} />
          </div>
        ))}
      </Section>

      <button onClick={save} disabled={saving} data-testid="franchise-save-btn-bottom" className="w-full rounded-full px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] bg-terracotta text-cream hover:bg-charcoal transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
        <Check size={14} /> {saving ? "Saving…" : "Save All Changes"}
      </button>
    </div>
  );
};

export default FranchiseManager;
